/**
 * Pure operations over the workspace and note Y.Docs.
 *
 * These live here, rather than inside the browser's WorkspaceStore, because
 * more than one client now writes to these documents: the browser, and the
 * assistant in apps/agent. Both must produce identically shaped items, so
 * both call these functions. Nothing here touches IndexedDB, React, or the
 * network; it is just document shape.
 *
 * INVARIANT: item fields live in a Y.Map per item so concurrent edits to
 * different fields (rename vs move) merge instead of clobbering.
 */
import * as Y from "yjs";
import { DOC_KEYS } from "./model.js";
import { compareSortKeys, sortKeyBetween } from "./sort.js";
export function workspaceItems(doc) {
    return doc.getMap(DOC_KEYS.workspaceItems);
}
/** The Y.Text holding a note's raw markdown. */
export function noteText(doc) {
    return doc.getText(DOC_KEYS.noteContent);
}
export function readItemFrom(id, m) {
    return {
        id,
        type: m.get("type") ?? "note",
        parentId: m.get("parentId") ?? null,
        title: m.get("title") ?? "",
        sortKey: m.get("sortKey") ?? "a0",
        folderNoteId: m.get("folderNoteId"),
        tags: m.get("tags") ?? [],
        createdAt: m.get("createdAt") ?? 0,
        updatedAt: m.get("updatedAt") ?? 0,
        trashedAt: m.get("trashedAt"),
        archivedAt: m.get("archivedAt"),
        aliases: m.get("aliases") ?? undefined,
        pinned: m.get("pinned") ?? undefined,
        colorKey: m.get("colorKey") ?? undefined,
        builtin: m.get("builtin") ?? undefined,
    };
}
/** Every item in the workspace, in sort-key order. */
export function readItems(doc) {
    const out = [];
    workspaceItems(doc).forEach((m, id) => out.push(readItemFrom(id, m)));
    out.sort((a, b) => compareSortKeys(a.sortKey, b.sortKey));
    return out;
}
export function getItem(doc, id) {
    const m = workspaceItems(doc).get(id);
    return m ? readItemFrom(id, m) : undefined;
}
/** Live (non-trashed, non-archived) children of a parent, sorted. */
export function childrenOf(doc, parentId) {
    return readItems(doc).filter((i) => i.parentId === parentId && !i.trashedAt && !i.archivedAt);
}
export function subtreeIds(doc, rootId) {
    const items = workspaceItems(doc);
    const out = [];
    const walk = (id) => {
        if (!items.has(id))
            return;
        out.push(id);
        items.forEach((m, childId) => {
            if (m.get("parentId") === id)
                walk(childId);
        });
    };
    walk(rootId);
    return out;
}
export function isDescendant(doc, candidate, ancestorId) {
    const items = workspaceItems(doc);
    let cur = candidate;
    let hops = 0;
    while (cur && hops++ < 100) {
        if (cur === ancestorId)
            return true;
        cur = items.get(cur)?.get("parentId") ?? null;
    }
    return false;
}
/**
 * Create an item with a caller-supplied id. The id is always minted by
 * whoever is creating the item (invariant 3), so it is a parameter here
 * rather than generated inside.
 */
export function createItemIn(doc, id, type, parentId, title = "") {
    const siblings = childrenOf(doc, parentId);
    const last = siblings[siblings.length - 1];
    const now = Date.now();
    doc.transact(() => {
        const m = new Y.Map();
        m.set("type", type);
        m.set("parentId", parentId);
        m.set("title", title);
        m.set("sortKey", sortKeyBetween(last?.sortKey ?? null, null));
        m.set("tags", []);
        m.set("createdAt", now);
        m.set("updatedAt", now);
        workspaceItems(doc).set(id, m);
    });
}
/** Mutate one item's fields and stamp updatedAt. No-op if it is gone. */
export function updateItem(doc, id, fn) {
    const m = workspaceItems(doc).get(id);
    if (!m)
        return;
    doc.transact(() => {
        fn(m);
        m.set("updatedAt", Date.now());
    });
}
export function renameItem(doc, id, title) {
    updateItem(doc, id, (m) => m.set("title", title));
}
export function setItemTags(doc, id, tags) {
    updateItem(doc, id, (m) => m.set("tags", tags));
}
/** Move under a new parent and/or between two siblings. Refuses cycles. */
export function moveItem(doc, id, parentId, beforeId) {
    if (id === parentId || isDescendant(doc, parentId, id))
        return;
    const siblings = childrenOf(doc, parentId).filter((s) => s.id !== id);
    let prev;
    let next;
    if (beforeId === undefined) {
        prev = siblings[siblings.length - 1];
    }
    else if (beforeId === null) {
        next = siblings[0];
    }
    else {
        const idx = siblings.findIndex((s) => s.id === beforeId);
        next = siblings[idx] ?? undefined;
        prev = idx > 0 ? siblings[idx - 1] : undefined;
    }
    updateItem(doc, id, (m) => {
        m.set("parentId", parentId);
        m.set("sortKey", sortKeyBetween(prev?.sortKey ?? null, next?.sortKey ?? null));
    });
}
/** Trash the whole subtree so nothing orphans. */
export function trashSubtree(doc, id) {
    const items = workspaceItems(doc);
    const now = Date.now();
    doc.transact(() => {
        for (const itemId of subtreeIds(doc, id)) {
            const m = items.get(itemId);
            if (m && !m.get("trashedAt")) {
                m.set("trashedAt", now);
                m.set("updatedAt", now);
            }
        }
    });
}
/** Archive a whole subtree. Unlike trash, archive has no retention expiry. */
export function archiveSubtree(doc, id) {
    const items = workspaceItems(doc);
    const now = Date.now();
    doc.transact(() => {
        for (const itemId of subtreeIds(doc, id)) {
            const m = items.get(itemId);
            if (m && !m.get("archivedAt")) {
                m.set("archivedAt", now);
                m.set("updatedAt", now);
            }
        }
    });
}
/** Restore an archived subtree and surface its root if its parent is hidden. */
export function unarchiveSubtree(doc, id) {
    const items = workspaceItems(doc);
    doc.transact(() => {
        for (const itemId of subtreeIds(doc, id)) {
            const m = items.get(itemId);
            if (m) {
                m.delete("archivedAt");
                m.set("updatedAt", Date.now());
            }
        }
        const m = items.get(id);
        const parentId = m?.get("parentId");
        if (parentId) {
            const parent = items.get(parentId);
            if (!parent || parent.get("trashedAt") || parent.get("archivedAt"))
                m.set("parentId", null);
        }
    });
}
/** Restore a trashed subtree and surface its root if its parent is gone. */
export function restoreTrashedSubtree(doc, id) {
    const items = workspaceItems(doc);
    doc.transact(() => {
        for (const itemId of subtreeIds(doc, id)) {
            const m = items.get(itemId);
            if (m) {
                m.delete("trashedAt");
                m.set("updatedAt", Date.now());
            }
        }
        const m = items.get(id);
        const parentId = m?.get("parentId");
        if (parentId) {
            const parent = items.get(parentId);
            if (!parent || parent.get("trashedAt"))
                m.set("parentId", null);
        }
    });
}
/**
 * Apply splices to a note's Y.Text as an ordinary edit.
 *
 * Deliberately NOT delete-all-then-insert. That is legal CRDT-wise but it
 * discards concurrent edits, resets every collaborator's cursor, and bloats
 * the update log. Splices are applied highest-index first so that earlier
 * indices, computed against the pre-edit text, stay valid.
 */
export function applyTextSplices(text, splices, origin) {
    if (splices.length === 0)
        return;
    const ordered = [...splices].sort((a, b) => b.index - a.index);
    const run = () => {
        for (const sp of ordered) {
            if (sp.delete > 0)
                text.delete(sp.index, sp.delete);
            if (sp.insert)
                text.insert(sp.index, sp.insert);
        }
    };
    const doc = text.doc;
    if (doc)
        doc.transact(run, origin);
    else
        run();
}
//# sourceMappingURL=doc.js.map